import {useState, type FormEvent} from 'react';
import {ErrorNotice, Loading, dateTime} from './components';
import {useHermes} from './lib/store';
import type {AccountDocument, HermesState} from './lib/types';
import {RuntimeSettings} from './hermes';
import {WhatsAppSettings} from './whatsapp-settings';
import {GoogleSettings} from './google-settings';
import {AccountIdentity} from './auth';
import {AccountProfileSettings} from './account-profile-settings';
import {ApplicationSettings} from './application-settings';
import {MessagePrefixSettings} from './message-prefix-settings';
import {PRODUCT_ENTRY_LABEL} from '../src/product';
import {StorageUsage} from './storage-usage';
import {VirtualMachineSettings} from './virtual-machine-settings';
import {AccountDataResetSettings} from './account-data-reset-settings';
import {APP_BASE, Link} from './router';

type Actions = ReturnType<typeof useHermes>['actions'];
type Props = {state: HermesState; actions: Actions};
type Draft = {name: string; content: string; expectedRevision: AccountDocument['revision'] | null};
const newDraft = (): Draft => ({name: '', content: '', expectedRevision: null});
const editDraft = (document: AccountDocument): Draft => ({name: document.name, content: document.content, expectedRevision: document.revision});
export function AccountSettingsContent({state, actions}: Props) {
  const settings = state.accountSettings.data;
  return <div className="account-settings">
    <ErrorNotice error={state.accountSettings.error}/>
    {state.accountSettings.status === 'loading' && !settings && <Loading/>}
    <section className="settings-section"><div className="settings-description"><h2>Sign-in</h2>
    </div><AccountIdentity state={state} actions={actions}/></section>
    <AccountProfileSettings key={`profile:${state.session.data?.accountId}`} state={state} actions={actions}/>
    <VirtualMachineSettings key={`vm:${state.session.data?.accountId}`} state={state} actions={actions}/>
    <GoogleSettings key={`google:${state.session.data?.accountId}`} state={state} actions={actions}/>
    <ApplicationSettings key={`applications:${state.session.data?.accountId}`} state={state} actions={actions}/>
    <section className="settings-section"><div className="settings-description"><h2>Memory</h2>
      <p>Keep useful work details and preferences across chats.</p>
      <p>Head of Eight saves and recalls useful details. It passes relevant context to the team.</p>
    </div>{state.accountSettings.status === 'ready' && settings && <div>
      <p className="settings-key-status">{settings.memory.provisioned && 'Your account memory is set up.'}</p>
      <p>Learned memory uses separate storage. It is not part of your file allowance.</p>
      <p>{settings.modelKey.configured ? 'Memory uses the provider selected for your account default.' : 'Contact your administrator to configure model access.'}</p>
      <p>Memory requests use model tokens. They are not included in the agent task cost display.</p>
    </div>}</section>
    <RuntimeSettings state={state} actions={actions}/>
    <MessagePrefixSettings key={`message-prefix:${state.session.data?.accountId}`} state={state} actions={actions}/>
    <WhatsAppSettings state={state} actions={actions}/>
    <AccountDataResetSettings key={`data-reset:${state.session.data?.accountId}`} state={state} actions={actions}/>
  </div>;
}

export function AccountFilesContent({state, actions}: Props) {
  const [draft, setDraft] = useState<Draft>(newDraft);
  const settings = state.accountSettings.data;
  const busy = state.operation.label !== null || state.accountSettings.status !== 'ready';
  const savedDocument = settings?.documents?.find(document => document.name === draft.name);
  const hasChanged = draft.expectedRevision !== null && savedDocument?.revision !== draft.expectedRevision;
  async function saveDocument(event: FormEvent): Promise<void> {
    event.preventDefault();
    try {
      const document = await actions.saveAccountDocument(draft.name, draft.content, draft.expectedRevision);
      setDraft(editDraft(document));
    } catch { /* Preserve the user's draft and display the full error in the operation notice. */ }
  }
  async function removeDocument(): Promise<void> {
    if (draft.expectedRevision === null) return;
    try {
      await actions.deleteAccountDocument(draft.name, draft.expectedRevision);
      setDraft(newDraft()); await actions.refreshStorage();
    } catch { /* The shared operation notice owns mutation errors. */ }
  }
  return <div className="account-files"><div className="files-summary"><StorageUsage state={state}/></div>
    <ErrorNotice error={state.accountSettings.error}/>
    {state.accountSettings.status === 'loading' && !settings && <Loading/>}
    {settings?.documents && settings.documentPolicy ? <section className="settings-section settings-documents"><div className="settings-description"><h2>Instructions and files</h2>
      <p>Read and change your saved instructions.</p>
      <p>Learned memory is managed through chat. It is not edited in this file list.</p>
      <p>Use chat to correct work in progress. An agent uses a file change when it next reads the file.</p>
      <p className="field-help">The editor supports {settings.documentPolicy.maximumDocuments} files. Each file can contain up to {settings.documentPolicy.maximumContentBytes.toLocaleString()} bytes of text.</p>
      <div className="document-list" aria-label="Saved instruction files">
        {settings.documents.map(document => <button className={`document-select ${draft.expectedRevision !== null && draft.name === document.name ? 'selected' : ''}`}
          type="button" key={document.name} disabled={busy} onClick={() => setDraft(editDraft(document))}>
          <strong>{document.name}</strong><span>Updated {dateTime(document.updatedAt)}</span></button>)}
        {settings.documents.length === 0 && <p className="field-help">You have no saved files here.</p>}
      </div><button type="button" className="text-button" disabled={busy} onClick={() => setDraft(newDraft())}>New file</button>
    </div><form className="document-editor" onSubmit={event => void saveDocument(event)}>
      {draft.expectedRevision === null ? <label className="field"><span>File name</span><input type="text" value={draft.name}
        onChange={event => setDraft(previous => ({...previous, name: event.target.value}))} required spellCheck={false}
        autoCapitalize="none" autoCorrect="off" disabled={busy}/></label> : <h3 className="document-name">{draft.name}</h3>}
      <label className="field"><span>File text</span><textarea rows={18} value={draft.content} spellCheck={false}
        onChange={event => setDraft(previous => ({...previous, content: event.target.value}))} disabled={busy}/></label>
      {hasChanged && <p className="document-conflict" role="alert">The saved file changed after you opened it. Your draft is still in this editor.</p>}
      <div className="button-row"><button type="submit" className="button" disabled={busy || !draft.name.trim()}>Save file</button>
        {draft.expectedRevision !== null && <button type="button" className="text-button" disabled={busy} onClick={() => void removeDocument()}>Delete file</button>}</div>
      {savedDocument && draft.expectedRevision !== null && <button type="button" className="text-button document-reload" disabled={busy}
        onClick={() => setDraft(editDraft(savedDocument))}>Replace draft with saved file</button>}
      <p className="field-help">Save your changes before you open another file. Ask your agents to work with other file types.</p>
    </form></section> : settings && <>
      <ErrorNotice error={{name:'FilesUnavailable',message:settings.documentState==='runtime-stopped'
        ? 'Your workspace is stopped. Reconnect it in Settings to access your files.'
        : 'Your files could not be loaded. Open Settings to check your workspace connection.'}}/>
      <Link className="button button-quiet" href={`${APP_BASE}/settings`}>Open Settings</Link>
    </>}
  </div>;
}
